import { CONCEPTS, type Concept, type ConceptId } from "../data/concepts";
import { FIGURES, type Figure, type FigureId } from "../data/figures";
import { analectById, analects, chapterName } from "./analects";
import { PASSAGES_BY_CONCEPT, PASSAGES_BY_FIGURE } from "./conceptTag";
import { GATE_ORDER } from "./gates";
import { countPassedGates, getRecord, isMastered, progressSummary } from "./progress";
import { gatePassed } from "./srs";
import type { GateId, ProgressState } from "./types";

/**
 * Progression that means something.
 *
 * Counting how many of 512 passages a student has clicked through says almost
 * nothing about whether they understand the Analects. These derived measures
 * answer better questions: how solid is your grasp of 仁? do you actually know
 * who 冉有 is? which chapter is your weakest? what should you do next?
 */

export type Level = 0 | 1 | 2 | 3 | 4 | 5;

export const LEVEL_LABEL: Record<Level, string> = {
  0: "未啟",
  1: "初識",
  2: "略通",
  3: "漸熟",
  4: "貫通",
  5: "精熟",
};

function levelOf(percent: number, touched: number): Level {
  if (touched === 0) return 0;
  if (percent >= 90) return 5;
  if (percent >= 70) return 4;
  if (percent >= 50) return 3;
  if (percent >= 25) return 2;
  return 1;
}

export interface DomainMastery {
  percent: number; // 0..100 of all gates across the domain's passages
  level: Level;
  total: number; // passages in the domain
  mastered: number; // passages with all four gates passed
  touched: number; // passages with at least one gate passed
}

function masteryOver(state: ProgressState, ids: readonly number[]): DomainMastery {
  let gates = 0;
  let mastered = 0;
  let touched = 0;
  const unique = Array.from(new Set(ids)).filter((id) => analectById.has(id));
  for (const id of unique) {
    const record = getRecord(state, id);
    const passed = countPassedGates(record);
    gates += passed;
    if (passed > 0) touched += 1;
    if (passed === GATE_ORDER.length) mastered += 1;
  }
  const totalGates = unique.length * GATE_ORDER.length;
  const percent = totalGates ? Math.round((gates / totalGates) * 100) : 0;
  return { percent, level: levelOf(percent, touched), total: unique.length, mastered, touched };
}

// ---- concepts ---------------------------------------------------------------

export interface ConceptProgress extends DomainMastery {
  concept: Concept;
}

export function conceptProgress(state: ProgressState, id: ConceptId): ConceptProgress {
  const concept = CONCEPTS.find((item) => item.id === id)!;
  return { concept, ...masteryOver(state, PASSAGES_BY_CONCEPT.get(id) ?? []) };
}

export function allConceptProgress(state: ProgressState): ConceptProgress[] {
  return CONCEPTS.map((concept) => conceptProgress(state, concept.id));
}

// ---- figures ----------------------------------------------------------------

export interface FigureProgress extends DomainMastery {
  figure: Figure;
}

export function figureProgress(state: ProgressState, id: FigureId): FigureProgress {
  const figure = FIGURES.find((item) => item.id === id)!;
  return { figure, ...masteryOver(state, PASSAGES_BY_FIGURE.get(id) ?? []) };
}

export function allFigureProgress(state: ProgressState): FigureProgress[] {
  return FIGURES.map((figure) => figureProgress(state, figure.id));
}

// ---- the learning path ------------------------------------------------------

export interface Stage {
  id: string;
  label: string;
  aim: string;
  chapters: number[];
}

/**
 * Five stages over the twenty chapters. The order is a recommendation, not a
 * lock: a student who wants 顏淵 on day one should be able to go there.
 */
export const STAGES: Stage[] = [
  { id: "s1", label: "入門", aim: "學、政、禮、仁四條主線先立起來", chapters: [1, 2, 3, 4] },
  { id: "s2", label: "觀人", aim: "由人物與評語，學會看人與看事", chapters: [5, 6, 7, 8] },
  { id: "s3", label: "進德", aim: "從自處到待人，德行的分寸", chapters: [9, 10, 11, 12] },
  { id: "s4", label: "致用", aim: "為政、應對、出處進退", chapters: [13, 14, 15, 16] },
  { id: "s5", label: "通貫", aim: "爭議、隱者、弟子後學與全書收束", chapters: [17, 18, 19, 20] },
];

export interface ChapterProgress {
  major: number;
  name: string;
  total: number;
  mastered: number;
  percent: number;
  level: Level;
}

const chapterIds = new Map<number, number[]>();
for (const analect of analects) {
  const list = chapterIds.get(analect.major) ?? [];
  list.push(analect.id);
  chapterIds.set(analect.major, list);
}

export function chapterProgress(state: ProgressState, major: number): ChapterProgress {
  const mastery = masteryOver(state, chapterIds.get(major) ?? []);
  return {
    major,
    name: chapterName(major),
    total: mastery.total,
    mastered: mastery.mastered,
    percent: mastery.percent,
    level: mastery.level,
  };
}

export interface StageProgress {
  stage: Stage;
  percent: number;
  chapters: ChapterProgress[];
  /** the previous stage is at least 略通 — the recommended, non-blocking order */
  recommended: boolean;
}

export function stageProgress(state: ProgressState): StageProgress[] {
  let previousPercent = 100;
  return STAGES.map((stage) => {
    const chapters = stage.chapters.map((major) => chapterProgress(state, major));
    const percent = chapters.length
      ? Math.round(chapters.reduce((sum, chapter) => sum + chapter.percent, 0) / chapters.length)
      : 0;
    const recommended = previousPercent >= 25;
    previousPercent = percent;
    return { stage, percent, chapters, recommended };
  });
}

// ---- rank ladder ------------------------------------------------------------

/**
 * The ladder is 2.4 — 志於學 to 從心所欲不踰矩. Using Confucius's own account
 * of his progress as the level track keeps the reward inside the material
 * instead of bolting on a generic XP bar.
 */
export interface Rank {
  id: string;
  label: string;
  gloss: string;
  atGates: number;
}

export const RANKS: Rank[] = [
  { id: "tongmeng", label: "童蒙", gloss: "才開卷，一切還新鮮", atGates: 0 },
  { id: "zhixue", label: "志學", gloss: "十有五而志於學", atGates: 40 },
  { id: "erli", label: "而立", gloss: "三十而立——站得住了", atGates: 200 },
  { id: "buhuo", label: "不惑", gloss: "四十而不惑——遇事不再搖擺", atGates: 520 },
  { id: "zhitianming", label: "知天命", gloss: "五十而知天命——知道界線在哪", atGates: 940 },
  { id: "ershun", label: "耳順", gloss: "六十而耳順——聽得進不同的話", atGates: 1400 },
  { id: "congxin", label: "從心", gloss: "七十而從心所欲，不踰矩", atGates: 1880 },
];

export interface RankState {
  rank: Rank;
  next: Rank | null;
  gates: number;
  toNext: number;
  percentToNext: number;
}

export function rankOf(state: ProgressState): RankState {
  const gates = progressSummary(state).gates;
  let index = 0;
  for (let i = 0; i < RANKS.length; i += 1) if (gates >= RANKS[i].atGates) index = i;
  const rank = RANKS[index];
  const next = RANKS[index + 1] ?? null;
  const span = next ? next.atGates - rank.atGates : 1;
  return {
    rank,
    next,
    gates,
    toNext: next ? Math.max(0, next.atGates - gates) : 0,
    percentToNext: next ? Math.min(100, Math.round(((gates - rank.atGates) / span) * 100)) : 100,
  };
}

// ---- what to do next --------------------------------------------------------

export type GoalKind = "review" | "wrong" | "chapter" | "concept" | "new";

export interface NextGoal {
  kind: GoalKind;
  title: string;
  detail: string;
  /** how many items this goal covers, for the progress pip */
  count: number;
}

/**
 * The single most useful thing to do right now. This is what the app's first
 * screen leads with — a student should never have to decide where to start.
 */
export function nextGoal(state: ProgressState, now: number = Date.now()): NextGoal {
  const summary = progressSummary(state, now);

  if (summary.due > 0) {
    return {
      kind: "review",
      title: "先過今日該複習的",
      detail: `有 ${summary.due} 道到期，趁記得再確認一次`,
      count: summary.due,
    };
  }

  const misses = state.misses?.length ?? 0;
  if (misses >= 3) {
    return {
      kind: "wrong",
      title: "把攻錯清一清",
      detail: `${misses} 道答錯的還沒攻回來`,
      count: misses,
    };
  }

  const weakest = allConceptProgress(state)
    .filter((entry) => entry.touched > 0)
    .sort((a, b) => a.percent - b.percent)[0];
  if (weakest && weakest.percent < 40) {
    return {
      kind: "concept",
      title: `補「${weakest.concept.label}」這一條`,
      detail: weakest.concept.question,
      count: weakest.total,
    };
  }

  const stages = stageProgress(state);
  const stage = stages.find((entry) => entry.percent < 100) ?? stages[stages.length - 1];
  const chapter = stage.chapters.find((entry) => entry.percent < 100) ?? stage.chapters[0];
  if (chapter && chapter.percent > 0) {
    return {
      kind: "chapter",
      title: `續讀《${chapter.name}》`,
      detail: `${stage.stage.label} · 已通 ${chapter.percent}%，還剩 ${chapter.total - chapter.mastered} 章`,
      count: chapter.total - chapter.mastered,
    };
  }

  return {
    kind: "new",
    title: chapter ? `開讀《${chapter.name}》` : "開始今天的修業",
    detail: chapter ? stage.stage.aim : "從《學而》第一章起步",
    count: chapter?.total ?? 0,
  };
}

// ---- per-passage detail -----------------------------------------------------

export interface GateStatus {
  gate: GateId;
  passed: boolean;
  attempts: number;
  correct: number;
  dueAt: number;
}

export function gateStatuses(state: ProgressState, analectId: number): GateStatus[] {
  const record = getRecord(state, analectId);
  return GATE_ORDER.map((gate) => {
    const srs = record.gates[gate];
    return {
      gate,
      passed: gatePassed(srs),
      attempts: srs.attempts,
      correct: srs.correct,
      dueAt: srs.dueAt,
    };
  });
}

export interface StudySnapshot {
  mastered: number;
  total: number;
  percent: number;
  due: number;
  gates: number;
  totalGates: number;
  rank: RankState;
  goal: NextGoal;
  dayStreak: number;
  bestDayStreak: number;
  concepts: ConceptProgress[];
  strongest: ConceptProgress | null;
  weakest: ConceptProgress | null;
}

/** Everything the home screen needs, computed once. */
export function studySnapshot(state: ProgressState, now: number = Date.now()): StudySnapshot {
  const summary = progressSummary(state, now);
  const concepts = allConceptProgress(state);
  const touched = concepts.filter((entry) => entry.touched > 0);
  const sorted = [...touched].sort((a, b) => b.percent - a.percent);
  return {
    mastered: summary.mastered,
    total: summary.total,
    percent: summary.percent,
    due: summary.due,
    gates: summary.gates,
    totalGates: summary.totalGates,
    rank: rankOf(state),
    goal: nextGoal(state, now),
    dayStreak: state.dayStreak ?? 0,
    bestDayStreak: state.bestDayStreak ?? 0,
    concepts,
    strongest: sorted[0] ?? null,
    weakest: sorted[sorted.length - 1] ?? null,
  };
}

export function masteredPassages(state: ProgressState): number[] {
  return analects.filter((analect) => isMastered(getRecord(state, analect.id))).map((analect) => analect.id);
}
